import { Helper } from "../helpers/helper";
import { CustomValidatorsUtility } from "./forms/custom-validators.utility";
import { FormWebhooksUtility } from "./forms/form-webhooks.utility";
import { UrlUtility } from "./url.utility";
import * as FormFieldsUtility from "./forms/form-fields.utility";
import * as FormDragDropUtility from "./forms/form-drag-drop.utility";
import { FormGroupItem, FormItem, ImisRestRequestFilter } from "../models/imis.models";
import { DataSourcesUtility } from "./dataSources.utility";
import { RestUtility } from "./rest.utility";

/**
 * Finds the iMIS form-builder generated element for a given table field by matching the trailing part of its `id` attribute, skipping any element that has a `validationGroup` set (typically a validator span rather than the actual input).
 * @param fieldName The name of the field as it appears in the element's id.
 * @param tableName Optional name of the table the field belongs to, prefixed onto the field name when matching. Omitted when the field is not part of a table.
 * @returns The matching HTML element, or `null` if no matching element without a validation group is found.
 */
function getElementByTableField(fieldName: string, tableName?: string): HTMLElement {
    let element: HTMLElement = null;
    let selectorParts = [tableName, fieldName].filter(function (part) { return !Helper.isStringNullOrEmpty(part) });
    let jPossibleElements = jQuery("[id$=_" + selectorParts.join("_") + "]");
    jPossibleElements.each(function () {
        if (this.validationGroup == null) {
            element = this;
        }
    });

    return element;
}

/**
 * Replaces a form-builder checkbox list field with a synthetic multi-select dropdown that mirrors and stays in sync with the underlying checkboxes: it is prepopulated with the currently checked options, hides the original checkbox list, and on change toggles the corresponding checkboxes (which triggers their own change events).
 * @param fieldName The name of the checkbox list field as it appears in the target element's id.
 * @param tableName The name of the table the field belongs to, used to locate the target element.
 * @param multiSelectId The id to assign to the generated `<select>` element. Defaults to `${fieldName}_MULTISELECT` when omitted.
 */
function convertCheckboxListToMultiSelect(fieldName: string, tableName: string, multiSelectId: string = fieldName + "_MULTISELECT") {
    let jElement = jQuery(getElementByTableField(fieldName, tableName));
    if (jElement.length > 0) {
        let jSelect = jElement.siblings("#" + multiSelectId);
        jSelect.remove();

        // setup multi select
        let alreadySelectedOptions = [];
        jSelect = jQuery("<select class='checkbox-list-to-multiselect' multiple></select>");
        jSelect.attr("id", multiSelectId);

        jElement.find("input[type=checkbox]").each(function () {
            // get the checkbox and its id and value
            let jCheckbox = jQuery(this);
            let optionId = jCheckbox.attr("id");
            let optionValue = jCheckbox.val();
            if (jCheckbox.prop("checked")) {
                alreadySelectedOptions.push(optionValue);
            }

            let jLabel = jCheckbox.siblings("label[for=" + optionId + "]");
            // clear out any child elements that wont render in the option tag
            let optionText = jLabel.clone().children().remove().end().text();
            // set value as text if empty
            if (Helper.isStringNullOrEmpty(optionText)) {
                optionText = optionValue.toString();
            }

            let jOption = jQuery("<option></option>");
            jOption.val(optionValue).data("id", optionId).text(optionText);

            jSelect.append(jOption);
        });

        // preset selected values
        jSelect.val(alreadySelectedOptions);

        // set on change to update all checkboxes
        jSelect.on("change", function () {
            jSelect.find("option").each(function () {
                let jOption = jQuery(this);
                let jCheckbox = jQuery("#" + jOption.data("id"));
                jCheckbox.prop("checked", jOption.prop("selected")).trigger("change");
            });
        });

        jElement.hide();
        jSelect.insertBefore(jElement);
    }
}

/**
 * Determines whether the current page reflects a successful form submission, checking for a visible success message on on-premise iMIS installs, or either a visible success message or a `Saved=true` URL parameter on iMIS cloud installs.
 * @returns `true` if the form submission was successful, otherwise `false`.
 */
function isFormSubmissionSuccess(): boolean {
    let formSubmissionSuccess = false;
    let formIsOnCloud = jQuery("[id$=_imisVersion]").length > 0;
    
    let jSuccessMessage = jQuery(".vaMessageAsi .AsiSuccess:not(.AsiError)");
    let successMessageCssVisibilityValue = jSuccessMessage.css("visibility");
    
    let isSuccessMessageVisible = jSuccessMessage.length > 0 && successMessageCssVisibilityValue != "hidden";
    if (!formIsOnCloud) {
        formSubmissionSuccess = isSuccessMessageVisible;
    } else {
        // two possible ways
        let isSavedParameterSet = UrlUtility.findUrlParameter("Saved") == "true";

        formSubmissionSuccess = isSavedParameterSet || isSuccessMessageVisible;
    }

    return formSubmissionSuccess;
}

/**
 * Retrieves the FormDesignerLibrary record for a given form, defaulting to the form identified by the `FormId` URL parameter when no id is supplied.
 * @param formId The id of the form to retrieve. Defaults to the `FormId` URL parameter when omitted.
 * @returns The form's data source record.
 */
async function getFormRecord(formId?: string | number): Promise<FormItem> {
    let currentFormId = formId || UrlUtility.findUrlParameter("FormId");
    let formData: FormItem = await DataSourcesUtility.getDataSourceRecord("FormDesignerLibrary", [currentFormId]);

    return formData;
}

/**
 * Persists changes to the form identified by the `formid` URL parameter by sending the given data to the FormDesignerLibrary REST endpoint via a PUT request.
 * @param data The form data to save.
 */
async function updateFormRecord(data: FormItem) {
    let id = UrlUtility.findUrlParameter("formid");
    await RestUtility.restApiSendData("/api/FormDesignerLibrary/" + id, data, "PUT");
}

/**
 * Retrieves all Form Designer form groups/types, fetching every page of results from the FormDesignerType REST endpoint.
 * @param parameters Optional filter/query parameters to narrow the request.
 * @returns All matching form group records.
 */
async function getFormGroups(parameters?: ImisRestRequestFilter<FormGroupItem>) {
    let formGroups = await RestUtility.restApiGetDataAll<FormGroupItem>("/api/FormDesignerType", parameters);
    if (formGroups == null) {
        throw new Error("Existing form group records could not be retrieved.");
    }
    
    return formGroups.Items;
}

/**
 * Retrieves all Form Designer forms, fetching every page of results from the FormDesignerLibrary REST endpoint. Defaults to active forms (`FormStatus: "A"`) unless overridden by `parameters`.
 * @param parameters Optional filter/query parameters to narrow the request.
 * @returns All matching form records.
 */
async function getFormRecords(parameters?: ImisRestRequestFilter<FormItem>) {
    let parametersWithDefaults = Object.assign(<FormItem>{ FormStatus: "A" }, parameters);
    let forms = await RestUtility.restApiGetDataAll<FormItem>("/api/FormDesignerLibrary", parametersWithDefaults);
    if (forms == null) {
        throw new Error("Existing form records could not be retrieved.");
    }

    return forms.Items;
}

export {
    getElementByTableField,
    convertCheckboxListToMultiSelect,
    isFormSubmissionSuccess,
    getFormRecord,
    updateFormRecord,
    getFormGroups,
    getFormRecords,
    FormFieldsUtility as Field,
    CustomValidatorsUtility as CustomValidator,
    FormWebhooksUtility as Webhook,
    FormDragDropUtility as DragDrop,
};
